import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const UCR = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Unified Carrier Registration 2026 | Renewal & Registration"
          description="Learn about UCR 2026 renewal and registration requirements, UCR fees, deadlines, exemptions, participating states, penalties, and steps to register online."
          keywords=""
          canonical="resources/excise-tax/unified-carrier-registration/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
            mb: 3,
          }}
        >
          Unified Carrier Registration
          <span className="highlight-container">
            <span className="highlight">(UCR)</span>
          </span>
          : Complete Guide <br />
          for Trucking Businesses
        </Typography>

        {/* <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=new-york-hut`}
          >
            E-file Now!
          </Button>
        </Box> */}
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/unified-carrier-registration/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        UCR Registration at a Glance
                      </Typography>

                      {/* Main List - All 4 items aligned in a straight line */}
                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        {/* Item 1 */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />
                          <span itemProp="name">
                            The{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Unified Carrier Registration (UCR)
                            </strong>{' '}
                            program requires motor carriers, freight brokers,
                            freight forwarders, and leasing companies operating
                            in{' '}
                            <strong style={{ fontWeight: 600 }}>
                              interstate or international commerce
                            </strong>{' '}
                            to register annually and pay a fee based on their
                            fleet size.
                          </span>
                        </li>

                        {/* Item 2 */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />
                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Renewal Due Date:
                            </strong>{' '}
                            UCR registration for the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              2026 registration year
                            </strong>{' '}
                            must be completed by{' '}
                            <strong style={{ fontWeight: 600 }}>
                              December 31, 2025
                            </strong>
                            . Registration generally opens on{' '}
                            <strong style={{ fontWeight: 600 }}>
                              October 1
                            </strong>{' '}
                            of the preceding year.
                          </span>
                        </li>

                        {/* Item 3 */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />
                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>Fees:</strong>{' '}
                            UCR fees are based on the number of commercial motor
                            vehicles operated in interstate commerce, ranging
                            from{' '}
                            <strong style={{ fontWeight: 600 }}>
                              $46 for 0–2 vehicles
                            </strong>{' '}
                            to{' '}
                            <strong style={{ fontWeight: 600 }}>
                              $44,836 for 1,001 or more vehicles
                            </strong>{' '}
                            for the 2026 registration year.
                          </span>
                        </li>

                        {/* Item 4 */}
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="4"
                            style={{ display: 'none' }}
                          />
                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Penalties:
                            </strong>{' '}
                            Failing to register or renew UCR can result in{' '}
                            <strong style={{ fontWeight: 600 }}>
                              state-issued citations, monetary penalties,
                              vehicle enforcement actions, and operational
                              disruptions
                            </strong>
                            . Penalties vary by state.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>
                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The Unified Carrier Registration (UCR) program is a
                      federally mandated registration system for individuals and
                      companies involved in interstate transportation. Under the
                      UCR program, motor carriers, freight brokers, freight
                      forwarders, and leasing companies must register annually
                      and pay a fee based on the size of their fleet.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The UCR program helps fund state enforcement of motor
                      carrier safety regulations and ensures that interstate
                      carriers remain compliant with federal transportation
                      requirements.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>Table of Contents</span>
                  </Typography>

                  <List
                    sx={{
                      mb: 5,
                      border: '1px solid #ccc',
                      borderRadius: 2,
                    }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to register for UCR?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#exempt" sx={{ color: '#0202ff' }}>
                          Who is exempt from UCR registration?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#commercial" sx={{ color: '#0202ff' }}>
                          What qualifies as a Commercial Motor Vehicle for UCR
                          Registration?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#renewal" sx={{ color: '#0202ff' }}>
                          Unified Carrier Registration Renewal
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#requirements" sx={{ color: '#0202ff' }}>
                          Unified Carrier Registration Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#online" sx={{ color: '#0202ff' }}>
                          How to Register UCR Online?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#fees" sx={{ color: '#0202ff' }}>
                          UCR fees for 2026
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#states" sx={{ color: '#0202ff' }}>
                          What are the states that don’t participate in UCR?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#vs" sx={{ color: '#0202ff' }}>
                          UCR vs Form 2290
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalties" sx={{ color: '#0202ff' }}>
                          Penalties for not registering for UCR
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText id="who">
                        <Link href="#enforcement" sx={{ color: '#0202ff' }}>
                          UCR Enforcement Penalties by State
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  Who needs to register for UCR?
                </Typography>
                <Typography id="certificate" variant="body1" sx={{ mb: 2 }}>
                  You must register for{' '}
                  <span style={{ fontWeight: 600 }}>
                    Unified Carrier Registration (UCR)
                  </span>{' '}
                  if your business operates in{' '}
                  <span style={{ fontWeight: 600 }}>
                    interstate or international commerce
                  </span>{' '}
                  and falls into one of these categories:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>Motor carriers (for-hire or private)</li>
                  <li>Freight forwarders</li>
                  <li>Freight brokers</li>
                  <li>Leasing companies</li>
                  <li>
                    Canadian and Mexican motor carriers operating commercial
                    vehicles within the United States.
                  </li>
                </Box>
                <Alert
                  id="exempt"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid #ed6c02' }}
                >
                  <b>Note:</b> Not sure whether your business is required to
                  register for UCR? Use the{' '}
                  <Link
                    target="_blank"
                    href="https://plan.ucr.gov/do-i-need-to-register/"
                    sx={{ color: '#0202ff' }}
                  >
                    quick eligibility questionnaire
                  </Link>{' '}
                  to determine if UCR registration applies to your operation.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  Who is exempt from UCR registration?
                </Typography>
                <Typography id="certificate" variant="body1" sx={{ mb: 3 }}>
                  Not every transportation business is required to register
                  under the Unified Carrier Registration (UCR) program. The
                  following entities are generally exempt:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Intrastate carriers</span>{' '}
                    that operate exclusively within a single state and do not
                    transport interstate or international freight.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Private carriers of passengers
                    </span>
                    , such as churches, schools, and businesses transporting
                    their own employees, provided they are not operating as
                    for-hire passenger carriers.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Government agencies</span>{' '}
                    at the federal, state, local, or tribal level.{' '}
                  </li>
                  <li id="commercial">
                    <span style={{ fontWeight: 600 }}>
                      Recreational vehicles(RV)
                    </span>{' '}
                    that used for non-business, non-commercial purposes.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  What qualifies as a Commercial Motor Vehicle for UCR
                  Registration?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For UCR registration purposes, a vehicle is generally
                  considered a commercial motor vehicle if it is used in
                  interstate or international commerce and meets{' '}
                  <span style={{ fontWeight: 600 }}>one or more</span> of the
                  following criteria:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Has a gross vehicle weight rating or gross vehicle weight of
                    at least 10,001 pounds, whichever is greater, or
                  </li>
                  <li>
                    When connected to trailing equipment has a gross combination
                    weight rating or gross combination weight of at least 10,001
                    pounds, whichever is greater, or
                  </li>
                  <li>
                    Carries placarded amounts of hazardous materials, regardless
                    of the vehicle's weight, or
                  </li>
                  <li id="renewal">
                    Is designed to carry more than 10 passengers, including the
                    driver.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  Unified Carrier Registration Renewal:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    The Unified Carrier Registration 2026 renewal period begins
                    on <span style={{ fontWeight: 600 }}>October 1, 2025.</span>
                  </li>
                  <li>
                    Motor carriers, brokers, freight forwarders, and leasing
                    companies must complete their registration and pay the
                    required fees by{' '}
                    <span style={{ fontWeight: 600 }}>December 31, 2025</span>,
                    to remain compliant for the 2026 registration year.
                  </li>
                  <li id="requirements">
                    Failing to renew your UCR on time can lead to{' '}
                    <Link href="#ucr-enforcement" sx={{ color: '#0202ff' }}>
                      state enforcement actions
                    </Link>{' '}
                    and disrupt your business operations. Penalties vary by
                    state and may include fines of{' '}
                    <span style={{ fontWeight: 600 }}>$1,000 or more</span>,
                    along with possible restrictions on operating authority
                    until compliance is restored.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  Unified Carrier Registration Requirements
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Before registering, gather the following information:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>USDOT Number</li>
                  <li>Legal business name</li>
                  <li>Business address </li>
                  <li id="online">Contact information </li>
                  <li>Number of commercial motor vehicles in operation </li>
                  <li>Payment method for registration fees </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                    scrollMarginTop: 87,
                  }}
                >
                  How to Register UCR Online?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 1: Visit the Portal
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Go to the{' '}
                  <Link
                    target="_blank"
                    href="https://www.ucr.gov/"
                    sx={{ color: '#0202ff' }}
                  >
                    UCR
                  </Link>{' '}
                  Portal.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 2: Enter your Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your USDOT number (must be active) and current business
                  information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 3: Determine Fleet Size
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide your vehicle count that will be used in interstate
                  commerce.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 4: Pay the required fee
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your fee is based on the fleet size category assigned to your
                  business.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Step 5: Download certificate
                </Typography>
                <Typography id="fees" variant="body1" sx={{ mb: 3 }}>
                  Receive your UCR certificate once the payment is completed.
                  Keep proof of registration and payment for your records in
                  case of audits or inspections.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  UCR fees for 2026
                </Typography>

                <Typography id="changes" variant="body1" sx={{ mb: 3 }}>
                  UCR fees vary by fleet size. Here’s a quick overview for 2026:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="2026 UCR Fee Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Fleet Size
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            2026 UCR Fee
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell>0–2</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$46</span>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>3–5</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$138</span>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>6–20</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$276</span>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>21–100</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$963</span>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>101–1,000</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$4,592</span>
                          </TableCell>
                        </TableRow>

                        <TableRow
                          id="states"
                          sx={{ backgroundColor: '#f9f9f9' }}
                        >
                          <TableCell>1,001+</TableCell>
                          <TableCell>
                            <span style={{ fontWeight: 600 }}>$44,836</span>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  What are the states that don’t participate in UCR?
                </Typography>
                <Typography id="changes" variant="body1" sx={{ mb: 3 }}>
                  The following{' '}
                  <span style={{ fontWeight: 600 }}>9 states</span> and{' '}
                  <span style={{ fontWeight: 600 }}>District of Columbia</span>{' '}
                  do not participate in the UCR (Unified Carrier Registration)
                  Agreement:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>Arizona</li>
                  <li>Florida</li>
                  <li>Hawaii</li>
                  <li>Maryland</li>
                  <li>Nevada</li>
                  <li>New Jersey</li>
                  <li>Oregon</li>
                  <li>Vermont</li>
                  <li>Wyoming</li>
                </Box>
                <Alert
                  id="vs"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid #ed6c02' }}
                >
                  <span style={{ fontWeight: 600 }}>Important Note: </span> Even
                  if your trucking company is based in one of these
                  non-participating states, you may still be required to
                  register for UCR if you operate in interstate commerce. In
                  that case, you'll select a participating state as your "{' '}
                  <span style={{ fontWeight: 600 }}>base state</span>" for UCR
                  registration.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="paying"
                >
                  UCR vs Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Many truck owners confuse UCR registration with Form 2290
                  filing. While both are important compliance requirements, they
                  serve different purposes.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="UCR and Form 2290 Comparison Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 220 }}>
                            Feature
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            UCR
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 2290
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell>Purpose</TableCell>
                          <TableCell>
                            Interstate transportation registration
                          </TableCell>
                          <TableCell>
                            Heavy Vehicle Use Tax (
                            <Link
                              href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                              sx={{ color: '#0202ff' }}
                            >
                              HVUT
                            </Link>
                            ) filing
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Administered By</TableCell>
                          <TableCell>
                            UCR Plan and participating states
                          </TableCell>
                          <TableCell>Internal Revenue Service</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>Who Must File</TableCell>
                          <TableCell>
                            Interstate motor carriers, brokers, freight
                            forwarders, and leasing companies
                          </TableCell>
                          <TableCell>
                            Owners of vehicles with a taxable gross weight of
                            55,000 pounds or more
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Fee Type</TableCell>
                          <TableCell>Annual registration fee</TableCell>
                          <TableCell>Federal highway use tax</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>Renewal Period</TableCell>
                          <TableCell>Annually (due by December 31)</TableCell>
                          <TableCell>
                            Annually (due by August 31 for most vehicles)
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Proof Received</TableCell>
                          <TableCell>UCR registration confirmation</TableCell>
                          <TableCell>IRS-stamped Schedule 1</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  id="penalties"
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid #ed6c02' }}
                >
                  <span style={{ fontWeight: 600 }}>Notes:</span> Businesses
                  operating heavy vehicles in interstate commerce may need both
                  UCR registration and Form 2290 filing.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  Penalties for not registering for UCR
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Failure to comply with UCR requirements may result in:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>State-issued citations</li>
                  <li>Monetary penalties</li>
                  <li>Vehicle enforcement actions</li>
                  <li>Registration compliance issues</li>
                  <li id="ucr-enforcement">
                    Potential operational disruptions
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  States actively enforce UCR compliance during roadside
                  inspections and audits.
                </Typography>
                <Typography
                  id="enforcement"
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                >
                  UCR Enforcement Penalties by State
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="State Penalty Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 180 }}>
                            State (Code)
                          </TableCell>

                          <TableCell sx={{ color: '#444' }}>Penalty</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell>AL</TableCell>
                          <TableCell>
                            $100 for the first offense; $500 for subsequent
                            offenses; No Out-of-Service.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>AR</TableCell>
                          <TableCell>
                            $100–$500 for the first offense; $100–$1,000 for
                            subsequent offenses.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>CA</TableCell>
                          <TableCell>
                            Base fine of $300–$1,000, county-specific fees.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>CO</TableCell>
                          <TableCell>
                            $1,100 for the first offense; $2,200 for the second;
                            $3,300 for subsequent; No Out-of-Service.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>CT</TableCell>
                          <TableCell>Fine of $170</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>ID</TableCell>
                          <TableCell>
                            Jail for up to 6 months, or fine up to $1,000, or
                            both. Typical bond ~$300.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>IL</TableCell>
                          <TableCell>
                            $300 first offense; $500 second offense.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>IN</TableCell>
                          <TableCell>
                            $150 first offense; $1,000 subsequent offense.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>IA</TableCell>
                          <TableCell>Fine of $250</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>KS</TableCell>
                          <TableCell>
                            $300 roadside fine; $100–$1,000 audit fine (first
                            offense); $100–$5,000 (subsequent).
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>KY</TableCell>
                          <TableCell>$25–$250.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>LA</TableCell>
                          <TableCell>Fine of $250.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>ME</TableCell>
                          <TableCell>
                            $250 first offense; $500 for a second or subsequent
                            offense.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>MA</TableCell>
                          <TableCell>
                            $100 first offense, $200 subsequent offense.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>MI</TableCell>
                          <TableCell>Up to $500 for hire carriers.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>MN</TableCell>
                          <TableCell>
                            $100 fine, plus $78 in court costs.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>MS</TableCell>
                          <TableCell>
                            $100–$500 + county-specific court costs and
                            assessments.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>MT</TableCell>
                          <TableCell>Warning only (for now).</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>NE</TableCell>
                          <TableCell>
                            Class IV misdemeanor offense, carrying a minimum
                            fine of $100. Additional fines may apply if multiple
                            or ongoing violations are discovered.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>NH</TableCell>
                          <TableCell>
                            $100 + penalty (first offense); $500 + penalty
                            (subsequent offense).
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>NM</TableCell>
                          <TableCell>
                            $50 misdemeanor; up to $10,000/day with formal
                            proceeding.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>NY</TableCell>
                          <TableCell>
                            Up to $5,000 with formal proceedings.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>NC</TableCell>
                          <TableCell>$1,000 per offense.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>OH</TableCell>
                          <TableCell>
                            $500 per violation; $1,000 for non-payment; $5,000
                            for false information.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>OK</TableCell>
                          <TableCell>
                            $300 first offense; $400 second; $500 subsequent.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>RI</TableCell>
                          <TableCell>$300 fine.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>SC</TableCell>
                          <TableCell>$232.50 fine.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>SD</TableCell>
                          <TableCell>$154 fine.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>TN</TableCell>
                          <TableCell>
                            State base fine of $50, plus court costs that are
                            county specific.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>TX</TableCell>
                          <TableCell>
                            Class C misdemeanor; $1–$500 fine + court costs.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>UT</TableCell>
                          <TableCell>CVSA Inspections first time.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>VA</TableCell>
                          <TableCell>
                            Class IV misdemeanor; $250 fine.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>WA</TableCell>
                          <TableCell>$124 fine.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>WV</TableCell>
                          <TableCell>$100–$1,000 fine.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell>WI</TableCell>
                          <TableCell>$750 fine.</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources & References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        UCR website. For more information, check the UCR{' '}
                        <Link
                          target="_blank"
                          href="https://plan.ucr.gov/frequently-asked-questions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Faq
                        </Link>{' '}
                        , UCR{' '}
                        <Link
                          target="_blank"
                          href="https://irp.cdn-website.com/7bc682fd/files/uploaded/UCR%20Enforcement%20Penalties%20by%20State.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          state penalties
                        </Link>{' '}
                        ,{' '}
                        <Link
                          target="_blank"
                          href="https://plan.ucr.gov/fee-brackets/"
                          sx={{ color: '#0202ff' }}
                        >
                          Fee bracket
                        </Link>{' '}
                        , motor carriers (
                        <Link
                          target="_blank"
                          href="https://www.txdmv.gov/motor-carriers/unified-carrier-registration"
                          sx={{ color: '#0202ff' }}
                        >
                          MC
                        </Link>
                        ), and UCR{' '}
                        <Link
                          target="_blank"
                          href="https://plan.ucr.gov/"
                          sx={{ color: '#0202ff' }}
                        >
                          plan
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
    </AppLayout>
  );
};

export default UCR;
